<script lang="ts" setup>
import { computed } from 'vue';
import { Building2, ChevronsUpDown, MoreHorizontal, Plus, Settings, Trash2 } from 'lucide-vue-next';
import { toast } from 'vue-sonner';

import { useSidebar } from '@/components/ui/sidebar';
import { useTeamDialog } from '@/composables/useTeamDialog';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { useRingCentralStore } from '@/stores/ringcentral';

import type { Team } from './types';

const { teams } = defineProps<{
  teams: Team[];
}>();

// Define emits for team selection
const emit = defineEmits<{
  (e: 'teamSelected', team: Team): void;
}>();

const { isMobile, open, setOpenMobile } = useSidebar();
const authStore = useAuthStore();
const orgStore = useOrganizationStore();
const ringCentralStore = useRingCentralStore();
const { openAddTeamDialog, openManageTeamDialog } = useTeamDialog();

// Find initial active team based on current organization ID
function getTeamFromOrgId(orgId: string | null): Team {
  // If no teams available, return placeholder
  if (teams.length === 0) {
    return { name: 'No Organization', logo: Building2, plan: 'Add a Studio' };
  }

  // If no orgId selected, return first team
  if (!orgId) {
    return teams[0];
  }

  // Find team by ID - now uses proper organization IDs from the API
  const teamById = teams.find(team => team.id === orgId);
  if (teamById) {
    return teamById;
  }

  // If no match found, return first team (fallback)
  return teams[0];
}

// Make activeTeam reactive to both organization changes and team list updates
const activeTeam = computed(() => {
  return getTeamFromOrgId(orgStore.currentOrgId);
});

// Keep a separate ref for UI state management (for dropdown selection)
const selectedTeam = ref<Team>(activeTeam.value);

// Control the main teams dropdown state
const isTeamsDropdownOpen = ref(false);

// Watch for changes in the computed activeTeam to update selectedTeam
watch(
  activeTeam,
  newTeam => {
    selectedTeam.value = newTeam;
  },
  { immediate: true },
);

// Set active team function
function setActiveTeam(team: Team) {
  selectedTeam.value = team;
  // Emit event when team is selected
  emit('teamSelected', team);
  // Close sidebar on mobile after team selection
  if (isMobile.value) {
    setOpenMobile(false);
  }
}

// Dialog close control - prevent accidental closing during critical operations
// This will be passed to the dialog to control backdrop/escape closing
// Default to allow, child components will handle their own protection

const selectedTeamForDeletion = ref<Team | null>(null);
const showDeleteConfirmation = ref(false);
const deletingTeamIds = ref<Set<string>>(new Set());

// Computed property to check if a specific team is being deleted
function isTeamDeleting(teamId: string | undefined) {
  if (!teamId) {return false;}
  return deletingTeamIds.value.has(teamId);
}

// Check if the selected team for deletion is being deleted
const isDeletingSelectedTeam = computed(() => {
  return isTeamDeleting(selectedTeamForDeletion.value?.id);
});

// Compute reason why user cannot create organizations
const cannotCreateOrgReason = computed(() => {
  if (authStore.canCreateOrganizations) {
    return null;
  }

  const accountType = authStore.accountInfo?.accountType;
  if (accountType === 'SUB_ACCOUNT') {
    return 'Only primary account holders can create organizations. Please contact your account administrator.';
  }
  return 'You do not have permission to create organizations.';
});

function handleAddTeam() {
  // Close sidebar on mobile when opening dialog to prevent blocking
  if (isMobile.value) {
    setOpenMobile(false);
  }
  openAddTeamDialog();
}

function handleManageTeam(team?: Team) {
  // Close the teams dropdown to prevent visual overlap with dialog
  isTeamsDropdownOpen.value = false;

  // Close sidebar on mobile when opening dialog to prevent blocking
  if (isMobile.value) {
    setOpenMobile(false);
  }

  // Use the passed team's ID, or fall back to active team
  const targetTeam = team || activeTeam.value;
  const currentOrgId = targetTeam.id || orgStore.currentOrgId;

  if (!currentOrgId) {
    toast.error('No organization selected');
    return;
  }

  openManageTeamDialog(currentOrgId);
}

// Handle delete team
function handleDeleteTeam(team: Team) {
  // Close the teams dropdown to prevent visual overlap with dialog
  isTeamsDropdownOpen.value = false;

  selectedTeamForDeletion.value = team;
  showDeleteConfirmation.value = true;
}

async function confirmDeleteTeam() {
  if (!selectedTeamForDeletion.value?.id) {return;}

  const teamId = selectedTeamForDeletion.value.id;
  if (deletingTeamIds.value.has(teamId)) {return;}

  try {
    deletingTeamIds.value.add(teamId);
    console.log('🗑️ Deleting connection for organization:', teamId);

    const { ringCentralAPI } = await import('@/api/ringcentral-api');

    // Use the new smart delete organization endpoint
    const deleteResult = await ringCentralAPI.deleteOrganization(teamId);

    console.log('✅ Organization relationship removed successfully:', {
      organizationId: deleteResult.organizationId,
      deletedAt: deleteResult.deletedAt,
      organizationDeleted: deleteResult.organizationDeleted,
      userRelationsRemoved: deleteResult.cascadeDeleted.userRelations,
      connectionsPreserved: deleteResult.cascadeDeleted.connections === 0,
    });

    toast.success('Organization removed successfully', {
      position: 'top-center',
      description: `"${selectedTeamForDeletion.value.name}" has been removed from your account.`,
    });

    // Close confirmation dialog
    showDeleteConfirmation.value = false;
    selectedTeamForDeletion.value = null;

    // Refresh data instead of page reload
    await Promise.all([
      // Refresh RingCentral organizations
      ringCentralStore.getUserOrganizations(true),
      // Refresh user memberships from main API
      authStore.fetchUserMemberships(true),
      // Update organization store
      orgStore.loadUserOrganizations(),
    ]);

    // Organizations will be updated by the store refresh calls above

    console.log('🔄 Teams list updated after deletion');
  } catch (error) {
    console.error('❌ Failed to delete connection:', error);
    toast.error('Failed to remove organization', {
      position: 'top-center',
      description: error instanceof Error ? error.message : 'An unexpected error occurred',
    });
  } finally {
    deletingTeamIds.value.delete(teamId);
  }
}

function cancelDeleteTeam() {
  showDeleteConfirmation.value = false;
  selectedTeamForDeletion.value = null;
}
</script>

<template>
  <UiSidebarMenu>
    <UiSidebarMenuItem>
      <UiDropdownMenu v-model:open="isTeamsDropdownOpen">
        <UiDropdownMenuTrigger as-child>
          <UiSidebarMenuButton
            size="lg"
            class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <div
              class="flex items-center justify-center rounded-lg aspect-square size-8 bg-sidebar-primary text-sidebar-primary-foreground"
            >
              <component :is="selectedTeam.logo" class="size-4" />
            </div>
            <div class="grid flex-1 text-sm leading-tight text-left">
              <span class="font-semibold truncate">{{ selectedTeam.name }}</span>
              <span class="text-xs truncate">{{ selectedTeam.plan }}</span>
            </div>
            <ChevronsUpDown class="ml-auto" />
          </UiSidebarMenuButton>
        </UiDropdownMenuTrigger>
        <UiDropdownMenuContent
          class="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          align="start"
          :side="isMobile || open ? 'bottom' : 'right'"
          :side-offset="4"
        >
          <template v-if="teams.length > 0">
            <UiDropdownMenuLabel class="text-xs text-muted-foreground">
              Organizations
            </UiDropdownMenuLabel>
            <UiDropdownMenuItem
              v-for="team in teams"
              :key="team.name"
              class="gap-2 p-2 group"
              @click="setActiveTeam(team)"
            >
              <div class="flex items-center justify-center border rounded-sm size-6">
                <component :is="team.logo" class="size-4 shrink-0" />
              </div>
              <div class="flex-1 truncate">
                {{ team.name }}
              </div>
              <!-- Three-Dot Actions Menu -->
              <div class="flex items-center ml-auto pl-2">
                <UiDropdownMenu>
                  <UiDropdownMenuTrigger as-child @click.stop>
                    <UiButton
                      variant="ghost"
                      size="sm"
                      class="size-6 p-0 opacity-50 hover:opacity-100 focus:ring-0 data-[state=open]:bg-accent data-[state=open]:opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100"
                    >
                      <MoreHorizontal class="size-4" />
                    </UiButton>
                  </UiDropdownMenuTrigger>
                  <UiDropdownMenuContent side="right" align="start" class="min-w-[140px]">
                    <UiDropdownMenuItem @click.stop="handleManageTeam(team)">
                      <Settings class="mr-2 size-4" />
                      <span>Settings</span>
                    </UiDropdownMenuItem>
                    <UiDropdownMenuItem
                      class="text-red-600 focus:bg-red-50 focus:text-red-700 dark:text-red-400 dark:focus:bg-red-950/50 dark:focus:text-red-300"
                      :disabled="isTeamDeleting(team.id)"
                      @click.stop="handleDeleteTeam(team)"
                    >
                      <Trash2 class="mr-2 size-4" />
                      <span>{{ isTeamDeleting(team.id) ? 'Deleting...' : 'Delete' }}</span>
                    </UiDropdownMenuItem>
                  </UiDropdownMenuContent>
                </UiDropdownMenu>
              </div>
            </UiDropdownMenuItem>
            <UiDropdownMenuSeparator />
          </template>

          <!-- Add New Organization button - disabled for sub-accounts with tooltip -->
          <UiTooltipProvider :delay-duration="200">
            <UiTooltip :disabled="!cannotCreateOrgReason">
              <UiTooltipTrigger as-child>
                <UiDropdownMenuItem
                  class="gap-2 p-2"
                  :disabled="!!cannotCreateOrgReason"
                  @click.stop="handleAddTeam"
                >
                  <div class="flex items-center justify-center border rounded-md size-6 bg-background">
                    <Plus class="size-4" />
                  </div>
                  <div class="font-medium text-muted-foreground">Add new</div>
                </UiDropdownMenuItem>
              </UiTooltipTrigger>
              <UiTooltipContent v-if="cannotCreateOrgReason" side="right" class="max-w-[250px]">
                <p class="text-sm">{{ cannotCreateOrgReason }}</p>
              </UiTooltipContent>
            </UiTooltip>
          </UiTooltipProvider>
        </UiDropdownMenuContent>
      </UiDropdownMenu>
    </UiSidebarMenuItem>

    <!-- Delete Confirmation Dialog -->
    <UiDialog v-model:open="showDeleteConfirmation">
      <UiDialogContent>
        <UiDialogHeader>
          <UiDialogTitle>Delete Organization</UiDialogTitle>
          <UiDialogDescription>
            Are you sure you want to delete "{{ selectedTeamForDeletion?.name }}"? This action
            cannot be undone and will permanently remove all associated data.
          </UiDialogDescription>
        </UiDialogHeader>

        <div class="rounded-lg bg-red-50 border border-red-200 p-4 mt-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Warning</h3>
              <div class="mt-1 text-sm text-red-700">
                <ul class="list-disc list-inside space-y-1">
                  <li>All call data and analytics will be permanently deleted</li>
                  <li>RingCentral integration will be disconnected</li>
                  <li>This action cannot be reversed</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-between mt-6 pt-4 border-t border-border">
          <UiButton variant="outline" :disabled="isDeletingSelectedTeam" @click="cancelDeleteTeam">
            Cancel
          </UiButton>
          <UiButton
            variant="destructive"
            :loading="isDeletingSelectedTeam"
            @click="confirmDeleteTeam"
          >
            {{ isDeletingSelectedTeam ? 'Deleting...' : 'Delete Organization' }}
          </UiButton>
        </div>
      </UiDialogContent>
    </UiDialog>
  </UiSidebarMenu>
</template>
